import React, { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { WizardData } from "./OfferWizard";
import {
  getOfferFiles,
  getAvailableFilesForOffer,
  attachFileToOffer,
  updateOfferFile,
  removeFileFromOffer,
  reorderOfferFiles,
} from "../../services/api";

interface FilesStepProps {
  data: WizardData;
  updateData: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrev: () => void;
}

interface FileItem {
  id: string;
  displayName: string;
  description: string;
  originalFileName: string;
  fileSize: number;
  fileType: string;
  fileUrl: string;
}

interface OfferFileItem {
  id: string;
  file: FileItem;
  customDescription: string;
  sortOrder: number;
  attachedAt: string;
}

// Draggable file row component
const DraggableFileRow: React.FC<{
  offerFile: OfferFileItem;
  editingFileId: string | null;
  editingDescription: string;
  onStartEdit: (offerFile: OfferFileItem) => void;
  onSaveDescription: (offerFileId: string) => void;
  onCancelEdit: () => void;
  onDetachFile: (offerFileId: string) => void;
  setEditingDescription: (value: string) => void;
  isLoading: boolean;
}> = ({
  offerFile,
  editingFileId,
  editingDescription,
  onStartEdit,
  onSaveDescription,
  onCancelEdit,
  onDetachFile,
  setEditingDescription,
  isLoading,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: offerFile.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes("pdf")) return "📄";
    if (fileType.includes("excel") || fileType.includes("spreadsheet"))
      return "📊";
    if (fileType.includes("word") || fileType.includes("document")) return "📝";
    return "📎";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mb-3 p-4 border rounded-lg transition-shadow ${
        isDragging
          ? "shadow-lg border-blue-300 bg-blue-50"
          : "border-gray-200 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          <div
            {...attributes}
            {...listeners}
            className="mt-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
          >
            ⋮⋮
          </div>
          <span className="text-2xl">
            {getFileIcon(offerFile.file.fileType)}
          </span>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900">
              {offerFile.file.displayName}
            </h4>

            {/* Description editing */}
            {editingFileId === offerFile.id ? (
              <div className="mt-2">
                <textarea
                  value={editingDescription}
                  onChange={(e) => setEditingDescription(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  rows={2}
                  placeholder="Opis dla tej oferty..."
                />
                <div className="flex space-x-2 mt-2">
                  <button
                    onClick={() => onSaveDescription(offerFile.id)}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                  >
                    Zapisz
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                  >
                    Anuluj
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1">
                <p
                  className="text-sm text-gray-600 cursor-pointer hover:text-blue-600"
                  onClick={() => onStartEdit(offerFile)}
                >
                  {offerFile.customDescription || "Kliknij, aby dodać opis..."}
                </p>
              </div>
            )}

            <p className="text-xs text-gray-400 mt-1">
              {offerFile.file.originalFileName} •{" "}
              {formatFileSize(offerFile.file.fileSize)}
            </p>
          </div>
        </div>
        <button
          onClick={() => onDetachFile(offerFile.id)}
          disabled={isLoading}
          className="ml-3 px-2 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
        >
          Usuń
        </button>
      </div>
    </div>
  );
};

const FilesStep: React.FC<FilesStepProps> = ({ data, onNext, onPrev }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [availableFiles, setAvailableFiles] = useState<FileItem[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<OfferFileItem[]>([]);
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editingDescription, setEditingDescription] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadFiles = useCallback(async () => {
    if (!data.offerId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [attached, available] = await Promise.all([
        getOfferFiles(data.offerId),
        getAvailableFilesForOffer(data.offerId),
      ]);

      setAttachedFiles(attached || []);
      setAvailableFiles(available || []);
    } catch (err: any) {
      console.error("Error loading files:", err);
      setError(err.response?.data?.message || "Błąd podczas ładowania plików");
    } finally {
      setIsLoading(false);
    }
  }, [data.offerId]);

  // Load files when component mounts
  useEffect(() => {
    if (data.offerId) {
      loadFiles();
    }
  }, [data.offerId, loadFiles]); // loadFiles is stable since it doesn't depend on external variables

  const handleAttachFile = async (fileId: string) => {
    if (!data.offerId) return;

    setIsLoading(true);
    setError(null);

    try {
      await attachFileToOffer(data.offerId, fileId);
      await loadFiles(); // Refresh both lists
    } catch (err: any) {
      console.error("Error attaching file:", err);
      setError(err.response?.data?.message || "Błąd podczas dodawania pliku");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDetachFile = async (offerFileId: string) => {
    if (!data.offerId) return;

    setIsLoading(true);
    setError(null);

    try {
      await removeFileFromOffer(data.offerId, offerFileId);
      await loadFiles(); // Refresh both lists
    } catch (err: any) {
      console.error("Error detaching file:", err);
      setError(err.response?.data?.message || "Błąd podczas usuwania pliku");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartEditDescription = (offerFile: OfferFileItem) => {
    setEditingFileId(offerFile.id);
    setEditingDescription(offerFile.customDescription);
  };

  const handleSaveDescription = async (offerFileId: string) => {
    if (!data.offerId) return;

    setError(null);

    try {
      await updateOfferFile(data.offerId, offerFileId, {
        customDescription: editingDescription,
      });

      // Update local state
      setAttachedFiles((prev) =>
        prev.map((file) =>
          file.id === offerFileId
            ? { ...file, customDescription: editingDescription }
            : file
        )
      );

      setEditingFileId(null);
      setEditingDescription("");
    } catch (err: any) {
      console.error("Error updating description:", err);
      setError(err.response?.data?.message || "Błąd podczas zapisywania opisu");
    }
  };

  const handleCancelEdit = () => {
    setEditingFileId(null);
    setEditingDescription("");
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || !data.offerId || active.id === over.id) return;

    const oldIndex = attachedFiles.findIndex((item) => item.id === active.id);
    const newIndex = attachedFiles.findIndex((item) => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const newAttachedFiles = arrayMove(attachedFiles, oldIndex, newIndex);

      // Update local state immediately for better UX
      setAttachedFiles(newAttachedFiles);

      // Send reorder request
      const fileIds = newAttachedFiles.map((item) => item.file.id);

      try {
        await reorderOfferFiles(data.offerId, fileIds);
      } catch (err: any) {
        console.error("Error reordering files:", err);
        setError(
          err.response?.data?.message || "Błąd podczas zmiany kolejności"
        );
        // Reload files to reset order if failed
        await loadFiles();
      }
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes("pdf")) return "📄";
    if (fileType.includes("excel") || fileType.includes("spreadsheet"))
      return "📊";
    if (fileType.includes("word") || fileType.includes("document")) return "📝";
    return "📎";
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isFileAttached = (fileId: string) => {
    return attachedFiles.some((attached) => attached.file.id === fileId);
  };

  if (isLoading && availableFiles.length === 0 && attachedFiles.length === 0) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
            <span className="text-gray-600">Ładowanie plików...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold mb-6">Pliki Dodatkowe</h2>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <p className="text-gray-600 mb-6">
          Dołącz pliki do oferty (PDF, XLS, DOCX), które klient będzie mógł
          pobrać razem z ofertą.
        </p>

        {/* Available Files Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">
            Dostępne pliki (
            {availableFiles.filter((f) => !isFileAttached(f.id)).length})
          </h3>
          <div className="space-y-3">
            {availableFiles.filter((f) => !isFileAttached(f.id)).length ===
            0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Wszystkie dostępne pliki zostały już dodane do oferty.</p>
              </div>
            ) : (
              availableFiles
                .filter((file) => !isFileAttached(file.id))
                .map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">
                        {getFileIcon(file.fileType)}
                      </span>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {file.displayName}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {file.description}
                        </p>
                        <p className="text-xs text-gray-400">
                          {file.originalFileName} •{" "}
                          {formatFileSize(file.fileSize)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleAttachFile(file.id)}
                      disabled={isLoading}
                      className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      Dodaj
                    </button>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Attached Files Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">
            Pliki w ofercie ({attachedFiles.length})
          </h3>

          {attachedFiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nie dodano jeszcze żadnych plików do oferty.</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={attachedFiles.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div>
                  {attachedFiles.map((offerFile) => (
                    <DraggableFileRow
                      key={offerFile.id}
                      offerFile={offerFile}
                      editingFileId={editingFileId}
                      editingDescription={editingDescription}
                      onStartEdit={handleStartEditDescription}
                      onSaveDescription={handleSaveDescription}
                      onCancelEdit={handleCancelEdit}
                      onDetachFile={handleDetachFile}
                      setEditingDescription={setEditingDescription}
                      isLoading={isLoading}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <button
            onClick={onPrev}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Wstecz
          </button>
          <button
            onClick={onNext}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Ładowanie..." : "Dalej"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilesStep;
